import { Injectable } from '@angular/core';
import { ApiService } from '@app/shared/services/api.service';
import { extend } from 'lodash';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, first, map, tap } from 'rxjs/operators';

const featuresToMockLocally = {
  // THIS SHOULD BE EMPTY IN DEVELOP AND MODIFIED FOR LOCAL CONVENIENCE ONLY
  // IF YOU SEE A PR WHERE THIS IS NOT EMPTY CALL THE POLICE IMMEDIATELY
  // Example:
  'adminUI.v3.telephony.sites': true
};

/** ? */
@Injectable({
  providedIn: 'root'
})
export class FeatureService {
  private readonly features: { [id: string]: ReplaySubject<{ [id: string]: boolean }> } = {}; // features we've already retrieved

  constructor(private readonly api: ApiService) {}

  /**
   * Request a single feature toggle
   */
  public hasFeature(feature: string): Observable<boolean> {
    return this.getFeatureToggles([feature]).pipe(map((ft) => ft[feature]));
  }

  /**
   * Request the required feature toggles and combine with those that have already been fetched.
   */
  public getFeatureToggles(features: string[]): Observable<{ [id: string]: boolean }> {
    // which FTs do we need to fetch
    const need: string[] = features.filter(
      (feature) => !Object.keys(this.features).includes(feature)
    );
    // create needed Observables
    need.forEach((ft) => (this.features[ft] = new ReplaySubject<{ [id: string]: boolean }>(1)));

    // combine all internal feature Observables for returning
    const rtn = combineLatest(features.map((ft) => this.features[ft])).pipe(
      map((all) => extend<{ [id: string]: boolean }>({}, ...all, featuresToMockLocally)),
      first() // complete observable sequence for toPromise!
    );

    if (need.length > 0) {
      // make some API calls
      this.makeRequest(need).pipe(first()).subscribe();
    }

    return rtn;
  }

  private makeRequest(queue: string[]): Observable<{ [id: string]: boolean }> {
    const url = `/platform/api/v2/featuretoggles?feature=${queue.join('&feature=')}`;

    debug('features', 'features api request', url);

    return this.api.get(url).pipe(
      // create internal Observables
      tap((features) =>
        Object.keys(features).forEach((ft) => {
          this.features[ft].next({ [ft]: features[ft] });
        })
      ),
      catchError((error) => {
        console.error(`Error fetching featureToggles: ${error}`);
        queue.forEach((ft) => {
          // if there is an API error, publish out false values for all requested and remove from internal collection
          this.features[ft].next({ [ft]: false });
          delete this.features[ft];
        });

        return of({});
      })
    );
  }
}
